import type { AppError } from './types';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function joinMessage(msg: unknown): string | undefined {
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) return msg.filter((m) => typeof m === 'string').join(', ') || undefined;
  return undefined;
}

type HeadersInput = Headers | Record<string, string>;

function readHeader(headers: HeadersInput, name: string): string | undefined {
  if (typeof (headers as Headers).get === 'function') {
    const v = (headers as Headers).get(name);
    return v ?? undefined;
  }
  const record = headers as Record<string, string>;
  const target = name.toLowerCase();
  for (const key of Object.keys(record)) {
    if (key.toLowerCase() === target) {
      const v = record[key];
      return typeof v === 'string' && v.length > 0 ? v : undefined;
    }
  }
  return undefined;
}

function extractTraceId(headers: HeadersInput | undefined): string | undefined {
  if (!headers) return undefined;
  return (
    readHeader(headers, 'x-trace-id') ??
    readHeader(headers, 'x-request-id') ??
    readHeader(headers, 'traceparent')
  );
}

export interface NormalizeApiErrorOptions {
  headers?: HeadersInput;
}

export function normalizeApiError(
  error: unknown,
  options?: NormalizeApiErrorOptions,
): AppError {
  const traceId = extractTraceId(options?.headers);
  const withTrace = (base: AppError): AppError =>
    traceId ? { ...base, traceId } : base;

  if (error === null || error === undefined) {
    return withTrace({ code: 'UNKNOWN_ERROR' });
  }

  if (typeof error === 'string') {
    return withTrace({ code: 'UNKNOWN_ERROR', message: error });
  }

  if (!isObject(error)) {
    return withTrace({ code: 'UNKNOWN_ERROR' });
  }

  // Envelope core crudo: { success: false, error: { code, message, detail } }
  if (error.success === false && isObject(error.error)) {
    const inner = error.error;
    const httpStatus =
      typeof error.httpStatus === 'number'
        ? error.httpStatus
        : typeof error.status === 'number'
          ? error.status
          : undefined;
    return withTrace({
      code: (typeof inner.code === 'string' ? inner.code : 'UNKNOWN_ERROR') as AppError['code'],
      httpStatus,
      message: typeof inner.message === 'string' ? inner.message : undefined,
      details: inner.detail,
    });
  }

  // Nest body (estándar o con code): { statusCode, message, error?, code?, details? }
  if (typeof error.statusCode === 'number') {
    const hasCode = typeof error.code === 'string';
    return withTrace({
      code: hasCode ? (error.code as AppError['code']) : 'UNKNOWN_ERROR',
      httpStatus: error.statusCode,
      message: joinMessage(error.message),
      details: hasCode ? error.details : error.error,
    });
  }

  // Network error / Error instance sin response
  if (error instanceof Error || typeof error.message === 'string') {
    return withTrace({
      code: 'NETWORK_ERROR',
      message: typeof error.message === 'string' ? error.message : undefined,
    });
  }

  return withTrace({ code: 'UNKNOWN_ERROR' });
}
